/**
 * electricalFacts.tsx — four different facts about a circuit, kept VISIBLY apart.
 *
 * WHY THIS FILE EXISTS RATHER THAN A GENERIC LIST
 * -----------------------------------------------
 * "A cable runs from the panel to the motor" is four claims, not one, and
 * collapsing them is how an engineering mistake gets made:
 *
 *   • a LOGICAL SIGNAL: a control loop says this input belongs to that output;
 *   • an ELECTRICAL SUPPLY: a conductor energises a device at a voltage;
 *   • a SCHEMATIC ROUTE: a line drawn between two points on a sheet;
 *   • a PHYSICAL ROUTE: a real path through a real duct, with a length, a fill
 *     and a voltage drop.
 *
 * They are routinely confused, and the confusion is invisible on a drawing: a
 * schematic route drawn the same colour as a power conductor is not a power
 * conductor, and a logical connection that appears to run through a duct is not a
 * physical one. P50's oracle exists to catch exactly this, and it cannot be caught
 * by a UI that renders one undifferentiated "connections" list.
 *
 * So the four are four PANELS, each bound to its own Engine capability, each
 * stating in its own words that it is not the other three. None is derived from
 * another, and none is computed here: the physical panel reads the Engine's own
 * route calculation, including the states where the Engine says a value is
 * unresolved.
 *
 * WHAT THIS FILE WILL NOT DO
 * --------------------------
 * It will not derive a physical length from a drawn line, fill a duct from a
 * cross-section it computed, or produce a voltage drop from a current and a
 * resistance. Every number on this page is a number the Engine returned, and where
 * the Engine returned an unresolved state that state is displayed as unresolved
 * rather than filled in with a default. A missing basis must never become a zero:
 * a zero is a claim, and a default is a lie with a decimal point.
 */

import { useMemo } from "react";
import { useClient } from "../../api/ClientContext";
import { buildReadRequest, inspect, type ActionPlan } from "../../api/capabilityAction";
import type { CapabilityDescriptor, CapabilityManifest } from "../../api/capabilityManifest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { ValueTree } from "../capabilities/ValueTree";
import { readCurrentness, currentnessStatement } from "../surfaces/currentness";

/** One of the four facts, and the Engine capability that is the authority for it. */
export interface ElectricalFact {
  readonly key: string;
  readonly title: string;
  /** The Engine capability this fact is read from. Named when unpublished. */
  readonly capabilityId: string;
  /**
   * What this fact IS, and — more importantly — what it is not.
   *
   * The negative half is load-bearing and is written per fact rather than once,
   * because the whole failure is the merge. A single shared disclaimer is a
   * disclaimer nobody reads next to the number they are about to act on.
   */
  readonly is: string;
  readonly isNot: string;
}

export const ELECTRICAL_FACTS: readonly ElectricalFact[] = Object.freeze([
  {
    key: "logical-signal",
    title: "Logical signal",
    capabilityId: "query/control.trace",
    is: "That an input belongs to a control output, and the loop that joins them.",
    isNot: "Not an electrical supply, and not a physical path. A signal drawn beside a power conductor is not one.",
  },
  {
    key: "electrical-supply",
    title: "Electrical supply",
    capabilityId: "query/electrical.supplies",
    is: "Which conductor energises a device, at what voltage, and from where.",
    isNot: "Not a signal and not a route. A supply is a source of energy; it says nothing about what a control system does with a value.",
  },
  {
    key: "schematic-route",
    title: "Schematic route",
    capabilityId: "operation/diagram/trace/add",
    is: "A connection drawn between two points on a sheet, in drawing coordinates.",
    isNot: "Not a physical route. Moving it on a sheet must not change an installed length, and this workbench never derives one from it.",
  },
  {
    key: "physical-route",
    title: "Physical route",
    capabilityId: "query/electrical.route-calculations",
    is: "A real path through a real assembly, with the length, fill and voltage drop the Engine derived from it.",
    isNot: "Not a schematic route and not a logical connection. Where the Engine reports a value as unresolved, this panel says so and leaves it unresolved.",
  },
]);

export interface ResolvedFact {
  readonly fact: ElectricalFact;
  readonly descriptor: CapabilityDescriptor | undefined;
  /** Undefined when the fact is not readable, or not readable through this browser. */
  readonly plan: ActionPlan | undefined;
  /** Why the fact is unreadable, naming what is missing. */
  readonly missing: string | undefined;
}

export function resolveElectricalFacts(manifest: CapabilityManifest): readonly ResolvedFact[] {
  return ELECTRICAL_FACTS.map(fact => {
    const descriptor = [...manifest.capabilities, ...manifest.projections].find(d => d.id === fact.capabilityId);
    if (descriptor === undefined) {
      return { fact, descriptor: undefined, plan: undefined, missing: `a capability published as "${fact.capabilityId}"` };
    }
    const resolution = inspect(manifest, descriptor);
    if (!resolution.addressable) {
      return { fact, descriptor, plan: undefined, missing: resolution.unaddressable.unavailable.missing };
    }
    if (!resolution.plan.phases.includes("read")) {
      return {
        fact,
        descriptor,
        plan: undefined,
        missing: `a READ route for "${fact.capabilityId}", which the Engine publishes as a mutation`,
      };
    }
    return { fact, descriptor, plan: resolution.plan, missing: undefined };
  });
}

function FactPanel({ projectId, entry }: { projectId: string; entry: ResolvedFact }): JSX.Element {
  const client = useClient();
  const retrieved = useAsync(
    async () => (entry.plan === undefined ? undefined : client.capabilityRead(projectId, buildReadRequest(entry.plan, {}).body)),
    [client, entry.plan, projectId],
    { enabled: entry.plan !== undefined },
  );
  const currentness = useMemo(
    () => (retrieved.data === undefined || entry.descriptor === undefined
      ? undefined
      : readCurrentness(entry.descriptor, retrieved.data)),
    [entry.descriptor, retrieved.data],
  );

  return (
    // The wrapper carries the identity hooks, because a real browser test must be
    // able to address ONE fact without matching on heading text — and because a
    // heading can be restyled into something the matcher cannot see.
    <div data-fact={entry.fact.key} data-capability={entry.fact.capabilityId}>
      <Panel title={entry.fact.title} testId="electrical-fact">
        <p>{entry.fact.is}</p>
        <p className="bad"><strong>{entry.fact.isNot}</strong></p>
        {entry.plan === undefined ? (
          <Empty>
            This fact cannot be read from this browser: {entry.missing}. Nothing is shown in its place, and nothing is
            derived from the other three.
          </Empty>
        ) : retrieved.loading ? (
          <Empty>Reading from the Engine…</Empty>
        ) : (
          <>
            <ErrorBanner error={retrieved.error} onDismiss={retrieved.reload} />
            {currentness !== undefined && (
              <div
                className="currentness"
                data-stage="stage-currentness"
                data-status-reading={currentness.status}
                data-status={currentness.statusValue ?? ""}
              >
                <p className={currentnessStatement(currentness).settled ? "muted" : "bad"}>
                  {currentnessStatement(currentness).text}
                </p>
              </div>
            )}
            {retrieved.data !== undefined && (
              <ValueTree
                value={retrieved.data}
                schema={entry.descriptor?.resultSchema}
                highlight={entry.descriptor?.presentation.primaryFields ?? []}
              />
            )}
          </>
        )}
      </Panel>
    </div>
  );
}

export function ElectricalFacts({ manifest, projectId }: { manifest: CapabilityManifest; projectId: string }): JSX.Element {
  const facts = useMemo(() => resolveElectricalFacts(manifest), [manifest]);
  return (
    <div className="electrical-facts" data-stage="electrical-facts">
      {facts.map(entry => <FactPanel key={entry.fact.key} projectId={projectId} entry={entry} />)}
    </div>
  );
}
